import type { IncidentSortField, SortDirection } from "./api";

/** The active list sort (F-011). */
export interface IncidentSort {
  field: IncidentSortField;
  dir: SortDirection;
}

/** The order the list had before sorting existed: newest first. */
export const DEFAULT_SORT: IncidentSort = { field: "createdAt", dir: "desc" };

/** Columns whose first click should show the newest first, not the oldest. */
const DESC_FIRST: ReadonlySet<IncidentSortField> = new Set(["createdAt", "discoveredAt"]);

/**
 * Header click: the active column flips its direction; another column becomes
 * active in its natural first direction - ascending, except the two points in
 * time, where the newest first is what somebody clicking "Entdeckt" wants.
 */
export function nextSort(current: IncidentSort, field: IncidentSortField): IncidentSort {
  if (current.field === field) {
    return { field, dir: current.dir === "asc" ? "desc" : "asc" };
  }
  return { field, dir: DESC_FIRST.has(field) ? "desc" : "asc" };
}

/** `aria-sort` of a column header under the active sort. */
export function ariaSort(
  current: IncidentSort,
  field: IncidentSortField,
): "ascending" | "descending" | "none" {
  if (current.field !== field) return "none";
  return current.dir === "asc" ? "ascending" : "descending";
}

/**
 * The Zeitraum inputs deliver calendar days (`YYYY-MM-DD`, the user's local
 * time). The API takes a half-open instant range on the Entdeckungszeitpunkt,
 * so "bis 03.09." means before the start of 04.09. local time - the whole of
 * the last day is included. An empty or malformed day contributes no bound.
 */
export function discoveredRange(
  fromDay: string,
  toDay: string,
): { discoveredFrom?: string; discoveredBefore?: string } {
  const from = localMidnight(fromDay, 0);
  const before = localMidnight(toDay, 1);
  return {
    ...(from ? { discoveredFrom: from } : {}),
    ...(before ? { discoveredBefore: before } : {}),
  };
}

function localMidnight(day: string, addDays: number): string | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!match) return undefined;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + addDays);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}
